import { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import RawMaterialService from "../../services/RawMaterials/RawMaterialService";
import { Dropdown } from "primereact/dropdown";
import { Tooltip } from "primereact/tooltip"; 


interface Props {
  posId: number;
  onSendRequest: (request: any) => void;
  onRejectRequest: (request: any) => void;
  setLoading: (loading: boolean) => void;
}

interface VendorInvoice {
  invoiceNumber: string;
  quantity: number;
  rawMaterialPurchaseId: number;
}

interface VendorStockSummary {
  vendorId: number;
  vendorName: string;
  totalQuantity: number;
  invoices: VendorInvoice[];
}

export const OutwardRawMaterialRequest: React.FC<Props> = ({
  posId,
  onSendRequest,
  onRejectRequest,
  setLoading
}) => {
  const toast = useRef<Toast>(null);
  const [requests, setRequests] = useState<any[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<any[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<any | null>(null);
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [documentDetails, setDocumentDetails] = useState("");
  const [note, setNote] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");

const [quantity, setQuantity] = useState<number>(0);
const [vendorList, setVendorList] = useState<VendorStockSummary[]>([]);
const [selectedVendorId, setSelectedVendorId] = useState<number | null>(null);
const [invoiceList, setInvoiceList] = useState<VendorInvoice[]>([]);
const [selectedInvoiceNumber, setSelectedInvoiceNumber] = useState<string>("");
const [rawMaterialPurchaseId, setRawMaterialPurchaseId] = useState<number | null>(null);


  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const response = await RawMaterialService.GetRawMaterialOutwardRequests(posId);
        setRequests(response || []);
        setFilteredRequests(response || []);
      } catch (error) {
        console.error("Error fetching Raw Material requests", error);
        setRequests([]);
        setFilteredRequests([]);
      }
    };
    fetchRequests();
  }, [posId]);

  const handleSearch = (value: string) => {
    setSearchKeyword(value);
    const lower = value.toLowerCase();
    setFilteredRequests(
      requests.filter(req =>
        req.rawMaterialName?.toLowerCase().includes(lower) ||
        req.requestFromPosCode?.toLowerCase().includes(lower)
      )
    );
  };

  const handleVendorChange = (vendorId: number) => {
  setSelectedVendorId(vendorId);
  const vendor = vendorList.find(v => v.vendorId === vendorId);
  const invoices = vendor?.invoices || [];
  setInvoiceList(invoices);
  setSelectedInvoiceNumber("");
  setRawMaterialPurchaseId(null);
};

const handleInvoiceChange = (invoiceNumber: string) => {
  setSelectedInvoiceNumber(invoiceNumber);
  const invoice = invoiceList.find(i => i.invoiceNumber === invoiceNumber);
  setRawMaterialPurchaseId(invoice?.rawMaterialPurchaseId || null);
};



const handleSubmitAccept = () => {
  const vendor = vendorList.find(v => v.vendorId === selectedVendorId);
  const invoice = invoiceList.find(i => i.invoiceNumber === selectedInvoiceNumber);

  if (!selectedVendorId || !vendor) {
    toast.current?.show({
      severity: "warn",
      summary: "Validation Error",
      detail: "Please select a valid Vendor.",
    });
    return;
  }
 
    if (!invoice || !selectedInvoiceNumber) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Please select an invoice number.",
      });
      return;
}

 if (quantity > invoice.quantity) {
    toast.current?.show({
      severity: "error",
      summary: "Insufficient Stock",
      detail: "Requested quantity exceeds available invoice stock.",
    });
    return;
  }

  if (!documentDetails.trim()) {
    toast.current?.show({
      severity: "warn",
      summary: "Validation Error",
      detail: "Document Details are required.",
    });
    return;
  }

  


  const updatedRequest = {
    ...selectedRequest,
    quantity,
    vendorId: vendor?.vendorId,
    vendorName: vendor?.vendorName,
    invoiceNumber: selectedInvoiceNumber,
    rawMaterialPurchaseId,
    documentDetails,
    note,
  };
  onSendRequest(updatedRequest);
  const removeId = selectedRequest.id;

setRequests(prev =>
  prev.filter(r => r.id !== removeId)
);

setFilteredRequests(prev =>
  prev.filter(r => r.id !== removeId)
);
  setShowAcceptDialog(false);
};


  const handleRejectRequest = () => {
    if (!note.trim()) {
      toast.current?.show({
        severity: "warn",
        summary: "Validation Error",
        detail: "Note is required for rejection",
      });
      return;
    }
    const rejected = {
      ...selectedRequest,
      note,
    };
    onRejectRequest(rejected);
    const removeId = selectedRequest.id;

setRequests(prev =>
  prev.filter(r => r.id !== removeId)
);

setFilteredRequests(prev =>
  prev.filter(r => r.id !== removeId)
);
    setShowRejectDialog(false);
  };

  const formatDate = (dateStr: string) => {
    const dt = new Date(dateStr);
    return `${dt.getDate().toString().padStart(2, '0')}-${(dt.getMonth() + 1)
      .toString()
      .padStart(2, '0')}-${dt.getFullYear()}`;
  };

  const actions = (rowData: any) => (
    <div className="flex gap-2">
      <Button
        label="Accept"
        className="px-1 py-2 text-sm"
        icon="pi pi-send"
     onClick={async () => {
  setSelectedRequest(rowData);
  setDocumentDetails("");
  setNote("");
  setQuantity(rowData.quantity);
  setSelectedVendorId(null); // clear selection
  setSelectedInvoiceNumber("");
  setRawMaterialPurchaseId(null);

  try {
    const vendorStock = await RawMaterialService.GetVendorandInvoiceWiseQty({
      rawMaterialId: rowData.rawMaterialId,
      posId: rowData.requestToPos
    });

    setVendorList(vendorStock || []);

    // Check if vendor & invoice exist
    const vendorExists = vendorStock.find((v: VendorStockSummary)  => v.vendorId === rowData.vendorId);
    const invoiceExists = vendorExists?.invoices.find((i: VendorInvoice) => i.invoiceNumber === rowData.invoiceNumber);

    if (vendorExists && invoiceExists) {
      setSelectedVendorId(rowData.vendorId);
      setInvoiceList(vendorExists.invoices);
      setSelectedInvoiceNumber(rowData.invoiceNumber);
      setRawMaterialPurchaseId(invoiceExists.rawMaterialPurchaseId);
    } else {
      setInvoiceList([]);
      // Show info toast or store a flag to show info icon
      // toast.current?.show({
      //   severity: "info",
      //   summary: "Vendor/Invoice not available",
      //   detail: "Previously selected Vendor/Invoice is not available. Please select manually.",
      // });
    }
  } catch (error) {
    toast.current?.show({
      severity: "error",
      summary: "Error",
      detail: "Failed to fetch vendor stock info.",
    });
  }

  setShowAcceptDialog(true);
}}

      />
      <Button
        label="Reject"
        className="px-1 py-2 text-sm"
        icon="pi pi-times"
        severity="danger"
        onClick={() => {
          setSelectedRequest(rowData);
          setNote("");
          setShowRejectDialog(true);
        }}
      />
    </div>
  );

  return (
    <>
      <Toast ref={toast} />
      <div className="flex justify-content-between align-items-center mb-3">
        <div className="p-input-icon-left w-full md:w-4">
          <i className="pi pi-search" />
          <InputText
            value={searchKeyword}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search Requests"
            className="w-full"
                        style={{
    border: "1px solid #75bb8aff",
    boxShadow: "0 0 0 1px #75bb8aff",
  }}
          />
        </div>
        {/* <Button
          icon="pi pi-file-excel"
          className="ml-3"
          text
          raised
        //   onClick={() => OutwardRequestExport(filteredRequests)}
        >
          Export Outward Requests
        </Button> */}
      </div>

      <DataTable 
      value={filteredRequests} 
      paginator 
      rows={10}
      rowsPerPageOptions={[10, 25, 50, 100]}
        emptyMessage="No outward requests found"
      className="p-datatable-sm"
      paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
      currentPageReportTemplate="{first} to {last} of {totalRecords}"
      >
        <Column field="requestedDate" header="Requested Date" body={(row) => formatDate(row.requestedDate)} sortable/>
        <Column field="rawMaterialName" header="Raw Material" style={{ width: "15%" }} sortable/>
        <Column field="quantity" header="Quantity" style={{ width: "15%" }} sortable/>
        <Column field="vendorName" header="Vendor" style={{ width: "15%" }} sortable/>
        <Column field="invoiceNumber" header="Invoice Number" sortable/>
        <Column field="requestFromPosCode" header="Requested By" sortable/>
        <Column header="Actions" body={actions} />
      </DataTable>

   
    <Dialog
  header="Accept Request - Add Details"
  visible={showAcceptDialog}
  onHide={() => setShowAcceptDialog(false)}
  style={{ width: "40vw" }}
  modal
>
  <div className="p-fluid">
    <div className="field">
      <label className="font-semibold text-sm block mb-1">Raw Material</label>
        <div className="p-inputtext p-component bg-gray-100 py-2 px-3 rounded">
          {selectedRequest?.rawMaterialName || ""}
        </div>
      {/* <InputText
        value={selectedRequest?.rawMaterialName || ""}
        disabled
        style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
      /> */}
    </div>

    <div className="field mt-3">
      <label className="font-semibold text-sm block mb-1">Quantity</label>
        <InputText
            type="number"
            value={quantity.toString()}  // Ensure it's a string
            onChange={(e) => setQuantity(Number(e.target.value))}
            style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
          />
          </div>

        <div className="field mt-3">
        <label className="font-semibold text-sm block mb-1">
        Vendor
        {!selectedVendorId && (
          <i
            className="pi pi-info-circle ml-2 text-blue-500 vendor-info-icon"
            data-pr-tooltip="Previously Requested vendor is unavailable. Please select another."
          />
        )}
      </label>
      <Dropdown
        value={selectedVendorId}
        options={vendorList}
        optionLabel="vendorName"
        optionValue="vendorId"
        style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
        onChange={(e) => handleVendorChange(e.value)}
        placeholder="Select Vendor"
        className="w-full"
        itemTemplate={(option) => (
          <div>
            {option.vendorName}
            <span className="ml-2 text-color-secondary text-sm">(Avail. Qty: {option.totalQuantity})</span>
          </div>
        )}
        valueTemplate={(option) =>
          option ? `${option.vendorName} (Avail. Qty: ${option.totalQuantity})` : "Select Vendor"
        }
      />
    </div>


    <div className="field mt-3">
    <label className="font-semibold text-sm block mb-1">
    Invoice Number
    {!selectedInvoiceNumber && (
      <i
        className="pi pi-info-circle ml-2 text-blue-500 invoice-info-icon"
        data-pr-tooltip="Previously Requested invoice is unavailable. Please select another."
      />
    )}
  </label>
    <Dropdown
      value={selectedInvoiceNumber}
      options={invoiceList}
      optionLabel="invoiceNumber"
      optionValue="invoiceNumber"
      onChange={(e) => handleInvoiceChange(e.value)}
      style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
      placeholder="Select Invoice"
      className="w-full"
      itemTemplate={(option) => (
        <div>
          {option.invoiceNumber} - {option.quantity} Qty
        </div>
      )}
      valueTemplate={(option) =>
        option ? `${option.invoiceNumber} - ${option.quantity} Qty` : "Select Invoice"
      }
    />
  </div>


    <div className="field mt-3">
      <label className="font-semibold text-sm block mb-1">Document Details *</label>
      <InputText
        value={documentDetails}
        onChange={(e) => setDocumentDetails(e.target.value)}
        placeholder="Enter document details"
        style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
      />
    </div>

    <div className="field mt-3">
      <label className="font-semibold text-sm block mb-1">Note (optional)</label>
      <InputText
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Enter note"
        maxLength={255}
        style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
      />
    </div>

    <div className="flex justify-content-end gap-2 mt-4">
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="p-button-secondary"
        onClick={() => setShowAcceptDialog(false)}
      />
      <Button
        label="Submit"
        icon="pi pi-check"
        onClick={handleSubmitAccept}
      />
    </div>
  </div>
        <Tooltip target=".vendor-info-icon" position="right" />
        <Tooltip target=".invoice-info-icon" position="right" />
</Dialog>

      <Dialog
        header="Reject Request - Add Note"
        visible={showRejectDialog}
        onHide={() => setShowRejectDialog(false)}
        style={{ width: "30vw" }}
        modal
      >
        <div className="p-fluid">
          <div className="field">
            <label htmlFor="rejectNote">Note *</label>
            <InputText
              id="rejectNote"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Enter reason for rejection"
              required
              style={{ border: '1px solid #ced4da', borderRadius: '4px' }}
              maxLength={255}
            />
          </div>

          <div className="flex justify-content-end gap-2 mt-4">
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-secondary"
              onClick={() => setShowRejectDialog(false)}
            />
            <Button
              label="Reject"
              icon="pi pi-check"
              severity="danger"
              onClick={handleRejectRequest}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};
